import { useCallback, useEffect, useRef, useState } from 'react';
import type { Read } from './api';

/** Under this many characters the server answers nothing, so the field does not ask. */
export const SEARCH_MIN_CHARS = 2;
/**
 * The pause after a keystroke before asking. «Cerco…» shows at once (feedback within 200 ms,
 * report §9.1); the pause only spares the server one request per letter.
 */
export const SEARCH_DELAY_MS = 150;

export type SearchState<T> =
  | { readonly status: 'idle'; readonly items: readonly T[] }
  | { readonly status: 'searching'; readonly items: readonly T[] }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | { readonly status: 'error'; readonly items: readonly T[] };

/**
 * A server search as the person types: idle under two characters, «searching» from the keystroke,
 * then the answer or the error. A slower, older answer never overwrites a newer one.
 */
export function useServerSearch<T>(
  query: string,
  search: (q: string) => Promise<Read<readonly T[]>>,
): SearchState<T> & { readonly retry: () => void } {
  const [state, setState] = useState<SearchState<T>>({ status: 'idle', items: [] });
  const [attempt, setAttempt] = useState(0);
  const run = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const text = query.trim();
    const current = ++run.current;
    if (text.length < SEARCH_MIN_CHARS) {
      setState({ status: 'idle', items: [] });
      return;
    }
    setState((prev) => ({ status: 'searching', items: prev.items }));
    const timer = setTimeout(() => {
      void searchRef.current(text).then((outcome) => {
        if (current !== run.current) return;
        setState(
          outcome.ok ? { status: 'ready', items: outcome.value } : { status: 'error', items: [] },
        );
      });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
